// Admin override for order assignment (2026-08-11).
//
// Agents pull work themselves; this is the escape hatch for when that is not
// enough — someone is off sick, an order needs a specific person, or a claim
// has to be broken. Admin-only, and every action lands in the audit log.
//
// Deliberately plain: a picker and one button. Reassignment is rare, so it
// should be obvious and boring rather than clever.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ordersApi, usersApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const AGENT_ROLE_SLUG = 'confirmation-agent';

export function OrderAssignmentControl({
  shopifyOrderId,
  assignedAgentId,
}: {
  shopifyOrderId: string;
  assignedAgentId: string | null | undefined;
}) {
  const { t } = useT();
  const qc = useQueryClient();

  const { data: users } = useQuery({
    queryKey: ['staff-users'],
    queryFn: () => usersApi.list(),
    staleTime: 5 * 60_000,
  });

  // Only people who actually work the queue — offering the whole staff list
  // would let an order be parked on a designer who will never see it.
  const agents = (users?.users ?? []).filter(
    (u) => u.is_active && u.roles?.some((r) => r.slug === AGENT_ROLE_SLUG),
  );
  const holder = agents.find((a) => a.id === assignedAgentId);

  const done = () => {
    qc.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    qc.invalidateQueries({ queryKey: ['order-queue'] });
  };

  const assign = useMutation({
    mutationFn: (agentId: string | null) => ordersApi.assign(shopifyOrderId, agentId),
    onSuccess: done,
  });

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-gray-500">
        {assignedAgentId
          ? t('Prise en charge par {name}', {
              name: holder?.full_name ?? holder?.email ?? t('un agent'),
            })
          : t('Dans la file partagée')}
      </span>

      <select
        value={assignedAgentId ?? ''}
        disabled={assign.isPending}
        onChange={(e) => assign.mutate(e.target.value || null)}
        className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white disabled:opacity-50"
        aria-label={t('Assigner à un agent')}
      >
        <option value="">{t('— Dans la file —')}</option>
        {agents.map((a) => (
          <option key={a.id} value={a.id}>{a.full_name ?? a.email}</option>
        ))}
      </select>

      {assignedAgentId && (
        <button
          type="button"
          onClick={() => assign.mutate(null)}
          disabled={assign.isPending}
          className="px-2.5 py-1 rounded-lg border text-[11px] font-semibold disabled:opacity-50 text-[#6B5423] dark:text-adm-gold-ink"
          style={{ borderColor: 'rgba(201,162,74,.35)' }}
        >
          {t('Remettre dans la file')}
        </button>
      )}

      {assign.isError && (
        <span className="text-status-stuck">{(assign.error as Error).message}</span>
      )}
    </div>
  );
}
